// webapp/src/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthInitialized, setIsAuthInitialized] = useState(false);
    const [hasReauthenticated, setHasReauthenticated] = useState(false);
    const [nextPage, setNextPage] = useState('');
    const [loading, setLoading] = useState(true);
    const [isPremium, setIsPremium] = useState(false);
    const [billingTier, setBillingTier] = useState('free');
    const [premiumLoading, setPremiumLoading] = useState(false);

    useEffect(() => {
        // Set up Supabase auth listener
        const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            setCurrentUser(session?.user || null);
            setIsAuthInitialized(true);
            setLoading(false);
        });

        // Initial session check
        fetchUser();

        // Cleanup subscription
        return () => {
            if (authListener && authListener.subscription) {
                authListener.subscription.unsubscribe();
            }
        };
    }, []);

    async function fetchUser() {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            setCurrentUser(session?.user || null);
            setIsAuthInitialized(true);
            setLoading(false);
        } catch (error) {
            console.error("Error getting session:", error);
            setLoading(false);
        }
    }

    const markUserAsReauthenticated = () => {
        setHasReauthenticated(true);
    };

    const resetReauthenticationStatus = () => {
        setHasReauthenticated(false);
    };

    const setNextPageAfterReauth = (page) => {
        setNextPage(page);
    };

    const getNextPageAfterReauth = () => {
        return nextPage;
    };

    // Function to check premium status and billing tier from Supabase profiles
    const checkPremiumStatus = useCallback(async (force = false) => {
        if (!currentUser && !force) return false;

        setPremiumLoading(true);
        try {
            // Get billing tier from profiles table
            const { data, error } = await supabase
                .from('profiles')
                .select('billing_tier')
                .eq('id', currentUser.id)
                .single();

            if (error) {
                console.warn("Error checking billing tier:", error);
                setIsPremium(false);
                setBillingTier('free');
                return false;
            }

            const tier = data?.billing_tier || 'free';
            setBillingTier(tier);
            const premiumStatus = tier !== 'free';
            setIsPremium(premiumStatus);
            return premiumStatus;
        } catch (error) {
            console.error("Error checking premium status:", error);
            setIsPremium(false);
            setBillingTier('free');
            return false;
        } finally {
            setPremiumLoading(false);
        }
        }, [currentUser]); // include anything you read: currentUser is read above

    // Check premium status when user changes
    useEffect(() => {
        if (currentUser) {
            checkPremiumStatus();
        } else {
            setIsPremium(false);
        }
        }, [currentUser, checkPremiumStatus]);

    // Conditional rendering based on the loading state
    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            currentUser,
            isAuthInitialized,
            hasReauthenticated,
            markUserAsReauthenticated,
            resetReauthenticationStatus,
            setNextPageAfterReauth,
            getNextPageAfterReauth,
            checkPremiumStatus,
            isPremium,
            billingTier,
            premiumLoading,
        }}>
            {children}
        </AuthContext.Provider>
    );
};